"use client";

import { type ImgHTMLAttributes } from "react";
import { PinkImageFallback } from "@/components/PinkImageFallback";
import { cn } from "@/lib/utils";

export const FALLBACK_IMAGE_SRC = "";

type FallbackImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src?: string | null;
  fallbackSrc?: string;
  fallbackClassName?: string;
  contentClassName?: string;
};

function usableSrc(src: string | null | undefined) {
  const trimmed = src?.trim();
  return trimmed ? trimmed : null;
}

export function FallbackImage({
  src,
  alt,
  className,
  fallbackClassName,
  fallbackSrc: _fallbackSrc,
  contentClassName,
  onError,
  ...props
}: FallbackImageProps) {
  const initialSrc = usableSrc(src);

  if (!initialSrc) {
    return (
      <PinkImageFallback
        ariaLabel={`${alt ?? "Image"} - image unavailable`}
        headline={alt ?? "Image unavailable"}
        className={cn(className, fallbackClassName)}
        contentClassName={contentClassName}
      />
    );
  }

  return (
    <span className="relative block">
      <PinkImageFallback
        ariaHidden
        ariaLabel={`${alt ?? "Image"} - image unavailable`}
        headline={alt ?? "Image unavailable"}
        className={cn("absolute inset-0 h-full w-full", fallbackClassName)}
        contentClassName={contentClassName}
      />
      <img
        {...props}
        src={initialSrc}
        alt={alt}
        onError={(event) => {
          onError?.(event);
          const fallback = event.currentTarget.previousElementSibling;
          if (fallback instanceof HTMLElement) {
            fallback.removeAttribute("aria-hidden");
          }
          event.currentTarget.setAttribute("aria-hidden", "true");
          event.currentTarget.style.opacity = "0";
        }}
        className={cn("relative z-10", className)}
      />
    </span>
  );
}
